import { motion } from 'framer-motion'
import ScrollReveal from '../components/shared/ScrollReveal'
import GradientText from '../components/shared/GradientText'
import GlowButton from '../components/shared/GlowButton'
import './DraftPages.css'

const Icons = {
  book: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
      <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
    </svg>
  ),
  code: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="16 18 22 12 16 6" />
      <polyline points="8 6 2 12 8 18" />
    </svg>
  ),
  zap: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
    </svg>
  ),
  settings: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  ),
  database: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <ellipse cx="12" cy="5" rx="9" ry="3" />
      <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3" />
      <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" />
    </svg>
  ),
  shield: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  ),
  arrowRight: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <line x1="5" y1="12" x2="19" y2="12" />
      <polyline points="12 5 19 12 12 19" />
    </svg>
  ),
  check: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  )
}

const docCategories = [
  {
    icon: Icons.zap,
    title: 'Getting Started',
    description: 'Quick start guides to get your solar monitoring system up and running in minutes.',
    links: [
      { label: 'Request a Demo', href: '/demo' },
      { label: 'View Pricing Plans', href: '/pricing' },
      { label: 'Contact Sales', href: '/contact' }
    ]
  },
  {
    icon: Icons.code,
    title: 'API Reference',
    description: 'Complete API documentation for integrating AI4Solar with your existing systems.',
    links: [
      { label: 'Authentication', href: '/api#authentication' },
      { label: 'REST API Endpoints', href: '/api#endpoints' },
      { label: 'WebSocket Streams', href: '/api#websocket' },
      { label: 'Rate Limits', href: '/api#rate-limits' }
    ]
  },
  {
    icon: Icons.settings,
    title: 'Platform Features',
    description: 'Explore the comprehensive monitoring and analytics capabilities of AI4Solar.',
    links: [
      { label: 'Alert Management', href: '/features' },
      { label: 'Real-time Monitoring', href: '/platform' },
      { label: 'ML Predictions', href: '/features' }
    ]
  },
  {
    icon: Icons.database,
    title: 'Data & Analytics',
    description: 'Learn about data collection, storage, and the analytics powering our ML models.',
    links: [
      { label: 'Platform Overview', href: '/platform' },
      { label: 'Feature Details', href: '/features' },
      { label: 'About Our Technology', href: '/about' }
    ]
  }
]

const quickLinks = [
  { label: 'Request a Demo', description: 'See AI4Solar in action with a personalized walkthrough', href: '/demo' },
  { label: 'View Platform Features', description: 'Explore monitoring, alerts, and ML capabilities', href: '/features' },
  { label: 'API Documentation', description: 'Integrate AI4Solar with your existing systems', href: '/api' },
  { label: 'Contact Our Team', description: 'Get help with setup and configuration', href: '/contact' }
]

const apiEndpoints = [
  { method: 'GET', endpoint: '/api/v1/plants', description: 'List all solar plants' },
  { method: 'GET', endpoint: '/api/v1/plants/{id}/metrics', description: 'Get real-time metrics' },
  { method: 'POST', endpoint: '/api/v1/alerts', description: 'Create custom alert' },
  { method: 'GET', endpoint: '/api/v1/predictions', description: 'Get ML predictions' }
]

export default function Documentation() {
  return (
    <div className="draft-page docs-page">
      {/* Hero */}
      <section className="draft-hero">
        <div className="container">
          <ScrollReveal>
            <span className="draft-badge">Documentation</span>
          </ScrollReveal>
          <ScrollReveal delay={0.1}>
            <h1 className="draft-title">
              Developer{' '}
              <GradientText gradient="energy">Resources</GradientText>
            </h1>
          </ScrollReveal>
          <ScrollReveal delay={0.2}>
            <p className="draft-subtitle">
              Everything you need to integrate, configure, and maximize your AI4Solar platform.
              From quick start guides to comprehensive API documentation.
            </p>
          </ScrollReveal>
          <ScrollReveal delay={0.3}>
            <div className="cta-buttons" style={{ marginTop: 'var(--space-xl)' }}>
              <GlowButton href="#quick-start" variant="solar" size="large">
                Get Started
              </GlowButton>
              <GlowButton href="#api" variant="outline" size="large">
                API Reference
              </GlowButton>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Documentation Categories */}
      <section className="about-section">
        <div className="container">
          <ScrollReveal className="section-header">
            <h2>Documentation</h2>
            <p>Explore our comprehensive guides and references</p>
          </ScrollReveal>

          <div className="values-grid">
            {docCategories.map((category, i) => (
              <ScrollReveal key={i} delay={i * 0.1}>
                <motion.div className="value-card docs-card" whileHover={{ y: -5 }}>
                  <span className="value-icon">{category.icon}</span>
                  <h3>{category.title}</h3>
                  <p>{category.description}</p>
                  <ul className="docs-links">
                    {category.links.map((link, j) => (
                      <li key={j}>
                        <a href={link.href}>
                          {link.label}
                          <span className="link-arrow">{Icons.arrowRight}</span>
                        </a>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Quick Links */}
      <section className="about-section about-dark" id="quick-start">
        <div className="container">
          <ScrollReveal className="section-header">
            <h2>Quick Start Guides</h2>
            <p>Get up and running with these step-by-step tutorials</p>
          </ScrollReveal>

          <div className="comparison-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))' }}>
            {quickLinks.map((link, i) => (
              <ScrollReveal key={i} delay={i * 0.1}>
                <a href={link.href} style={{ textDecoration: 'none' }}>
                  <motion.div className="comparison-card" whileHover={{ y: -5 }}>
                    <span className="cc-value" style={{ fontSize: '1.25rem' }}>{link.label}</span>
                    <span className="cc-label">{link.description}</span>
                  </motion.div>
                </a>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* API Preview */}
      <section className="about-section" id="api">
        <div className="container">
          <ScrollReveal className="section-header">
            <h2>API Reference</h2>
            <p>RESTful API with real-time WebSocket support</p>
          </ScrollReveal>

          <ScrollReveal delay={0.1}>
            <div className="api-preview">
              <div className="api-header">
                <span className="api-badge">REST API v1</span>
                <span className="api-base-url">Base URL provided upon registration</span>
              </div>
              <div className="api-endpoints">
                {apiEndpoints.map((endpoint, i) => (
                  <div key={i} className="api-endpoint">
                    <span className={`api-method method-${endpoint.method.toLowerCase()}`}>
                      {endpoint.method}
                    </span>
                    <code className="api-path">{endpoint.endpoint}</code>
                    <span className="api-desc">{endpoint.description}</span>
                  </div>
                ))}
              </div>
            </div>
          </ScrollReveal>

          <ScrollReveal delay={0.2}>
            <div className="api-features">
              <div className="api-feature">
                <span className="feature-icon">{Icons.shield}</span>
                <div>
                  <h4>Secure Authentication</h4>
                  <p>OAuth 2.0 and API key authentication with role-based access control</p>
                </div>
              </div>
              <div className="api-feature">
                <span className="feature-icon">{Icons.zap}</span>
                <div>
                  <h4>Real-time Data</h4>
                  <p>WebSocket streams for live metrics with sub-second latency</p>
                </div>
              </div>
              <div className="api-feature">
                <span className="feature-icon">{Icons.database}</span>
                <div>
                  <h4>Comprehensive Data</h4>
                  <p>Access to all metrics, predictions, alerts, and historical data</p>
                </div>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* SDK Section */}
      <section className="about-section about-dark">
        <div className="container">
          <ScrollReveal className="section-header">
            <h2>Integration Options</h2>
            <p>Multiple ways to connect with AI4Solar</p>
          </ScrollReveal>

          <div className="partners-grid">
            <ScrollReveal delay={0.1}>
              <a href="/api" style={{ textDecoration: 'none' }}>
                <motion.div className="partner-card" whileHover={{ scale: 1.02 }}>
                  <span className="partner-name">REST API</span>
                  <span className="partner-type">Full API documentation available</span>
                </motion.div>
              </a>
            </ScrollReveal>
            <ScrollReveal delay={0.2}>
              <a href="/api#websocket" style={{ textDecoration: 'none' }}>
                <motion.div className="partner-card" whileHover={{ scale: 1.02 }}>
                  <span className="partner-name">WebSocket Streams</span>
                  <span className="partner-type">Real-time data feeds</span>
                </motion.div>
              </a>
            </ScrollReveal>
            <ScrollReveal delay={0.3}>
              <a href="/contact" style={{ textDecoration: 'none' }}>
                <motion.div className="partner-card" whileHover={{ scale: 1.02 }}>
                  <span className="partner-name">Custom Integration</span>
                  <span className="partner-type">Contact us for enterprise solutions</span>
                </motion.div>
              </a>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Support CTA */}
      <section className="draft-cta">
        <div className="container">
          <ScrollReveal className="draft-cta-content">
            <h2>Need Help?</h2>
            <p>
              Our technical support team is ready to assist with integration questions,
              troubleshooting, and custom implementation guidance.
            </p>
            <div className="cta-buttons">
              <GlowButton href="/contact" variant="solar" size="large">
                Contact Support
              </GlowButton>
              <GlowButton href="/demo" variant="outline" size="large">
                Request Demo
              </GlowButton>
            </div>
          </ScrollReveal>
        </div>
      </section>
    </div>
  )
}
